import {
  MultiDocumentAgentTemplateType,
  PromptCategoryKey,
  PromptKey,
} from '@utils/enums.ts';
import {
  MultiDocumentAgentActions,
  MultiDocumentAgentTemplate,
} from '@app/types/admin';
import { NavigateFunction } from 'react-router-dom';
import { routes } from '@app/shared/constants/paths';
import {
  deleteDocumentGeneratorTemplate,
  duplicateDocumentTemplate,
  updateDocumentGeneratorTemplateOrder,
} from '@api/document-generator-template';
import {
  DepositionSummaryAppearance,
  PartyType,
} from '@app/types/deposition-summary';
import {
  deleteDepositionSummaryTemplate,
  duplicateDepositionSummaryTemplate,
  updateDepositionSummaryTemplateOrder,
} from '@api/deposition-summary-template';
import { DuplicateDocumentTemplateFormFields } from '@features/admin-multi-document-agent/utils/demand-letter/duplicate-template-validator';
import { ReferenceDocumentNode } from '@app/types/core/reference-document';
import { deleteAgent } from '@api/agents';
import {
  deleteUseCaseTemplate,
  duplicateUseCaseTemplate,
  updateUseCaseTemplateOrder,
} from '../../../services/api/use-case-template';

export const promptCategoryLabels: Record<string, string> = {
  [PromptCategoryKey.EXAMPLE_EXTRACTION]: 'Example Extraction Prompts',
  [PromptCategoryKey.EXTRACTION]: 'Extraction Prompts',
  [PromptCategoryKey.GENERATION]: 'Generation Prompts',
  [PromptCategoryKey.RAG]: 'RAG - Ground X Prompts',
};

export const promptKeyLabels: Record<string, string> = {
  [PromptKey.SYSTEM_EXTRACTION]: 'Overall Prompt',
  [PromptKey.MISTAKES_EXTRACTION]: 'Mistakes Prompt',
  [PromptKey.ICD_EXTRACTION]: 'ICD Prompt',
  [PromptKey.SYSTEM_GENERATION]: 'Overall Prompt',
  [PromptKey.SYSTEM_EXAMPLE_EXTRACTION]: 'Overall Prompt',
  [PromptKey.LIABILITY_RAG]: 'Liability Search Prompt',
};

export function buildTemplateActionsMap(
  navigate: NavigateFunction
): Record<MultiDocumentAgentTemplateType, MultiDocumentAgentActions> {
  return {
    [MultiDocumentAgentTemplateType.DEMAND_LETTER]: {
      onCreate: () =>
        navigate(routes.superAdmin.multiDocumentAgent.demandLetter.create),
      onEdit: (id: number) =>
        navigate(routes.superAdmin.multiDocumentAgent.demandLetter.edit(id)),
      onDuplicate: () => {},
      onDelete: deleteDocumentGeneratorTemplate,
      onMoveUp: (id) => updateDocumentGeneratorTemplateOrder(id, 'up'),
      onMoveDown: (id) => updateDocumentGeneratorTemplateOrder(id, 'down'),
    },
    [MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY]: {
      onCreate: () =>
        navigate(routes.superAdmin.multiDocumentAgent.depositionSummary.create),
      onEdit: (id: number) =>
        navigate(
          routes.superAdmin.multiDocumentAgent.depositionSummary.edit(id)
        ),
      onDuplicate: () => {},
      onDelete: deleteDepositionSummaryTemplate,
      onMoveUp: (id) => updateDepositionSummaryTemplateOrder(id, 'up'),
      onMoveDown: (id) => updateDepositionSummaryTemplateOrder(id, 'down'),
    },
    [MultiDocumentAgentTemplateType.USE_CASE]: {
      onCreate: () =>
        navigate(routes.superAdmin.multiDocumentAgent.useCase.create),
      onEdit: (id: number) =>
        navigate(routes.superAdmin.multiDocumentAgent.useCase.edit(id)),
      onDuplicate: () => {},
      onDelete: deleteUseCaseTemplate,
      onMoveUp: (id) => updateUseCaseTemplateOrder(id, 'up'),
      onMoveDown: (id) => updateUseCaseTemplateOrder(id, 'down'),
    },
    [MultiDocumentAgentTemplateType.CUSTOM_GPT]: {
      onCreate: () =>
        navigate(routes.superAdmin.multiDocumentAgent.customGpt.create),
      onEdit: (id: number) =>
        navigate(routes.superAdmin.multiDocumentAgent.customGpt.edit(id)),
      onDuplicate: () => {},
      onDelete: deleteAgent,
      onMoveUp: async () => {
        console.log('Move up not required for Custom GPT');
      },
      onMoveDown: async () => {
        console.log('Move down not required for Custom GPT');
      },
    },
  } as const;
}

export const createBlankAppearance = (
  party: PartyType
): DepositionSummaryAppearance => ({
  id: null,
  party,
  party_name: '',
  attorney_name: '',
  attorney_firm: '',
});

export const duplicateTemplate = async (
  template: MultiDocumentAgentTemplate,
  payload: DuplicateDocumentTemplateFormFields
) => {
  switch (template.template_type) {
    case MultiDocumentAgentTemplateType.DEMAND_LETTER:
      return duplicateDocumentTemplate(template.id, payload);

    case MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY:
      return duplicateDepositionSummaryTemplate(template.id, payload);

    case MultiDocumentAgentTemplateType.USE_CASE:
      return duplicateUseCaseTemplate(template.id, payload);

    case MultiDocumentAgentTemplateType.CUSTOM_GPT:
      // TODO: Implement duplicate logic for custom GPT templates
      throw new Error('Duplicate not implemented for custom GPT templates');

    default:
      throw new Error('Unsupported template type');
  }
};

export const findParentFolderId = (
  folderContents: Record<string, ReferenceDocumentNode[]>,
  itemId: string
): string | null =>
  Object.keys(folderContents).find((parentId) =>
    folderContents[parentId]?.some((child) => child.id === itemId)
  ) ?? null;

export function findItemInMap(
  targetId: string,
  folderId: string,
  folderContents: Record<string, ReferenceDocumentNode[]>
): ReferenceDocumentNode | undefined {
  const children = folderContents[folderId] || [];

  return children.reduce<ReferenceDocumentNode | undefined>((found, child) => {
    if (found) return found;
    if (child.id === targetId) return child;

    if (child.type === 'folder' && child.hasChildren) {
      return findItemInMap(targetId, child.id, folderContents);
    }

    return undefined;
  }, undefined);
}
